import React, { useState, useEffect, useMemo } from 'react';
import styles from './StandardPage.module.css';
import { PageFrame, PageHeader, FilterBar, FilterSelect, Button } from '@/components/ui';
import DataTable, { ColumnDef } from '@/components/utils/DataTable';
import './utils/cell-states.css';
import { Tooltip, TextField } from '@mui/material';
import { BrokerageComparisonRequest, BrokerComparisonData, SelectOption } from '@/types/client';
import { brokerComparisonApi } from '@/services/api';

const tradeTypeOptions: SelectOption[] = [
  { label: 'All', value: 'ALL' },
  { label: 'Buy', value: 'BUY' },
  { label: 'Sell', value: 'SELL' },
];

const BrokerComparison: React.FC = () => {
  const [fromDate, setFromDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [toDate, setToDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [selectedTradeType, setSelectedTradeType] = useState<SelectOption>({ label: 'All', value: 'ALL' });
  const [data, setData] = useState<BrokerComparisonData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [sortColumn, setSortColumn] = useState<keyof BrokerComparisonData>('total_notional');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Draft filter state: users can change these without triggering fetch
  const [pendingFromDate, setPendingFromDate] = useState<string>(fromDate);
  const [pendingToDate, setPendingToDate] = useState<string>(toDate);
  const [pendingTradeType, setPendingTradeType] = useState<SelectOption>(selectedTradeType);

  const hasChanges =
    pendingFromDate !== fromDate ||
    pendingToDate !== toDate ||
    pendingTradeType.value !== selectedTradeType.value;

  const isInvalidFilters =
    !pendingFromDate || !pendingToDate || pendingFromDate > pendingToDate;

  const applyFilters = () => {
    if (isInvalidFilters) return;
    setFromDate(pendingFromDate);
    setToDate(pendingToDate);
    setSelectedTradeType(pendingTradeType);
  };

  useEffect(() => {
    const fetchBrokerComparison = async () => {
      if (!fromDate || !toDate) return;
      else if (fromDate > toDate) return;

      setLoading(true);
      setError(null);

      try {
        const params: BrokerageComparisonRequest = {
          from_date: fromDate,
          to_date: toDate,
          trade_type: selectedTradeType.value,
        };

        const response = await brokerComparisonApi.getBrokerComparison(params);
        if (response.status >= 400) {
          throw new Error(response.statusText);
        }

        setData(response.data?.data?.broker_comparison_data || []);
      } catch (err: any) {
        const errorMessage = JSON.stringify(err.response?.data) || String(err) || 'Failed to load data';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchBrokerComparison();
  }, [fromDate, toDate, selectedTradeType]);

  const handleSort = (column: keyof BrokerComparisonData) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const numericColumns: (keyof BrokerComparisonData)[] = ['total_trades', 'total_notional', 'avg_notional', 'vwap_slippage', 'brokerage', 'total_slippage'];

  const sortedData = [...data].sort((a, b) => {
    let valA: any = a[sortColumn];
    let valB: any = b[sortColumn];

    if (numericColumns.includes(sortColumn)) {
      valA = Number(valA);
      valB = Number(valB);
    } else if (typeof valA === 'string') {
      valA = valA.toLowerCase();
      valB = typeof valB === 'string' ? valB.toLowerCase() : valB;
    }

    if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
    if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const formatNumber = (value: number, decimals = 2): string => {
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
  };

  /* Column definitions drive the head, the sort arrows and the cells — the
     table itself is <DataTable>, the same one every other list page uses. */
  const columns = useMemo<ColumnDef<BrokerComparisonData>[]>(() => {
    /* A slippage cell is read against zero: worse than benchmark is a loss,
       better is a gain. `cellClassName` tints the whole cell, as it did before —
       a class on the value alone would only tint the number's own box. */
    const slippageTone = (value: number) =>
      value > 0 ? 'cell-negative' : value < 0 ? 'cell-positive' : '';

    return [
      { key: 'broker_code', header: 'Broker Code', sortable: true, width: '140px',
        tooltip: 'Unique identifier for the broker',
        render: r => r.broker_code },
      { key: 'broker_name', header: 'Broker Name', sortable: true, width: '200px',
        tooltip: 'Name of the broker',
        render: r => r.broker_name },
      { key: 'total_trades', header: 'Total Trades', sortable: true, width: '130px', align: 'right',
        tooltip: 'Total number of trades executed',
        render: r => formatNumber(r.total_trades, 0) },
      { key: 'total_notional', header: 'Total Notional (₹)', sortable: true, width: '170px', align: 'right',
        tooltip: 'Total notional value of trades',
        render: r => formatNumber(r.total_notional, 0) },
      { key: 'avg_notional', header: 'Avg Notional (₹)', sortable: true, width: '170px', align: 'right',
        tooltip: 'Average notional value of trades',
        render: r => formatNumber(r.avg_notional, 0) },
      { key: 'vwap_slippage', header: 'VWAP Slippage (bps)', sortable: true, width: '180px', align: 'right',
        tooltip: 'Notional weighted average slippage',
        cellClassName: r => slippageTone(r.vwap_slippage),
        render: r => formatNumber(r.vwap_slippage) },
      { key: 'brokerage', header: 'Brokerage (bps)', sortable: true, width: '160px', align: 'right',
        tooltip: 'Notional weighted average brokerage fees',
        render: r => formatNumber(r.brokerage) },
      { key: 'total_slippage', header: 'Total Slippage (bps)', sortable: true, width: '180px', align: 'right',
        tooltip: 'Total slippage incurred (VWAP slippage + brokerage)',
        cellClassName: r => slippageTone(r.total_slippage),
        render: r => formatNumber(r.total_slippage) },
    ];
  }, []);

  return (
    <PageFrame header={<PageHeader title="Broker Comparison" subtitle="Execution quality and cost by broker over the selected period." />}>
      <FilterBar
        hideSearch
        filters={
          <>

          <div className={styles['filter-group']}>
            <TextField
              type="date"
              label="From Date"
              variant="outlined"
              size="small"
              value={pendingFromDate}
              onChange={(e) => setPendingFromDate(e.target.value)}
              slotProps={{
                inputLabel: { shrink: true }
              }}
            />
          </div>

          <div className={styles['filter-group']}>
            <TextField
              type="date"
              label="To Date"
              variant="outlined"
              size="small"
              value={pendingToDate}
              onChange={(e) => setPendingToDate(e.target.value)}
              slotProps={{
                inputLabel: { shrink: true }
              }}
            />
          </div>

          <div className={styles['filter-group']}>
            <FilterSelect
              label="Trade Type"
              options={tradeTypeOptions}
              value={pendingTradeType}
              onChange={value => setPendingTradeType(value ?? tradeTypeOptions[0])}
            />
          </div>
          <div className={styles['filter-actions']}>
            <Tooltip title={isInvalidFilters ? 'Please ensure From Date is not after To Date' : (!hasChanges ? 'No changes to apply' : 'Apply selected filters')}>
              <span>
                <Button
                  variant="primary"
                  onClick={applyFilters}
                  disabled={!hasChanges || isInvalidFilters}
                >
                  {loading? 'Applying...': 'Apply'}
                </Button>
              </span>
            </Tooltip>
          </div>
          </>
        }
      />

      <DataTable<BrokerComparisonData>
        embedded
        data={sortedData}
        columns={columns}
        rowKey={(row, i) => `${row.broker_code}-${i}`}
        totalCount={sortedData.length}
        loading={loading}
        error={error}
        currentPage={1}
        totalPages={1}
        onPageChange={() => undefined}
        pageSize={sortedData.length || 1}
        sortField={sortColumn}
        sortDir={sortDirection}
        onSort={field => handleSort(field as keyof BrokerComparisonData)}
        hideSearch
        loadingMessage="Loading broker comparison…"
        emptyMessage="No broker comparison data for the selected dates and trade type."
      />
    </PageFrame>
  );
};

export default BrokerComparison;